import type { VendorPriceLatest, VendorPriceRow } from '@morales/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

const RANGES = presets('d30', 'd7', 'd90', 'd365');
type LogSort = 'date' | 'vendor' | 'item' | 'price';

/** How far apart the vendors' latest prices are, as a share of the cheapest: where shopping around pays. */
export function spreadPct(l: VendorPriceLatest): number | null {
  if (l.vendors.length < 2) return null;
  const prices = l.vendors.map((v) => v.price);
  const lo = Math.min(...prices);
  return lo > 0 ? Math.round(((Math.max(...prices) - lo) / lo) * 1000) / 10 : null;
}

/**
 * Purchasing → Vendor prices (decision 79): every price any vendor gave us, from a texted offer, a price list, a bill
 * or the dock. "Latest by item" puts each vendor in a column with the cheapest in green; "All prices" is the log.
 */
export function VendorPricesPage() {
  const { t, intlLocale } = useLocale();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'log' ? 'log' : 'latest';
  const period = usePeriod(RANGES);
  const [vendor, setVendor] = useState('');
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: qk.vendorPrices(`${period.from}:${period.to}:${vendor}`),
    queryFn: ({ signal }) => api.vendorPrices({ from: period.from, to: period.to, ...(vendor ? { vendor } : {}) }, signal),
    placeholderData: keepPreviousData,
  });
  const data = query.data;
  const allVendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal) });
  const words = q.trim().toLowerCase();
  const hit = (name: string) => !words || name.toLowerCase().includes(words);
  const money = (v: number) => formatMoney(v, intlLocale);
  const sourceLabel = (s: VendorPriceRow['source']) => t(`priceSrc_${s}` as AppMessageKey);
  const setTab = (next: 'latest' | 'log') => {
    const p = new URLSearchParams(params);
    if (next === 'log') p.set('tab', 'log');
    else p.delete('tab');
    setParams(p);
  };

  // latest by item: a column per vendor (the chosen one only when filtered)
  const listed = data?.vendors ?? [];
  // a column for each vendor with a price on the sheet; the list line above names every vendor with a list
  const vendors = listed.filter((v) => (data?.latest ?? []).some((l) => l.vendors.some((x) => x.vendorId === v.id)));
  const latest = (data?.latest ?? []).filter((l) => hit(l.itemName));
  const latestCols: SheetColumn<VendorPriceLatest>[] = [
    { key: 'item', header: t('itemCol'), width: '220px', render: (l) => l.itemName },
    // each vendor: the price, then the day it was given in a column of its own (JL: less cluttered than under the price)
    ...vendors.flatMap((v): SheetColumn<VendorPriceLatest>[] => {
      const of = (l: VendorPriceLatest) => l.vendors.find((x) => x.vendorId === v.id);
      return [
        {
          key: `v:${v.id}`,
          header: v.name,
          width: '110px',
          align: 'right',
          render: (l) => {
            const p = of(l);
            return p ? <span title={sourceLabel(p.source)}>{money(p.price)}</span> : '';
          },
          cellClass: (l) => (l.cheapestVendorId === v.id ? 'good cheapest' : undefined),
        },
        {
          key: `d:${v.id}`,
          header: t('dateCol'),
          label: `${v.name} ${t('dateCol')}`,
          hint: `${v.name}: ${t('dateCol')}`,
          width: '100px',
          render: (l) => {
            const p = of(l);
            // older than the vendor's newest list: that list did not price it (decision 194)
            return p ? <span className={p.stale ? undefined : 'muted'} title={p.stale && v.listDate ? t('notOnLatestList').replace('{date}', formatDate(v.listDate, intlLocale)) : undefined}>{formatDate(p.date, intlLocale)}</span> : '';
          },
          cellClass: (l) => (of(l)?.stale ? 'warn' : undefined),
        },
        {
          key: `c:${v.id}`,
          header: t('changeCol'),
          label: `${v.name} ${t('changeCol')}`,
          hint: t('changeHint'),
          width: '96px',
          align: 'right',
          render: (l) => {
            const p = of(l);
            if (!p || p.change === null || p.previous === null || p.listPrice === null) return '';
            if (p.change === 'same') return <span className="muted">{t('priceSame')}</span>;
            const d = Math.round((p.listPrice - p.previous) * 100) / 100;
            return <span title={`${t('wasPrice')} ${money(p.previous)}`}>{p.change === 'up' ? '▲' : '▼'} {money(Math.abs(d))}</span>;
          },
          cellClass: (l) => (of(l)?.change === 'up' ? 'bad' : of(l)?.change === 'down' ? 'good' : undefined),
        },
      ];
    }),
    { key: 'spread', header: t('spreadCol'), hint: t('spreadHint'), width: '90px', align: 'right', render: (l) => (spreadPct(l) === null ? '' : `${spreadPct(l)}%`) },
  ];

  // the log
  const sorters = useMemo(
    () => ({
      date: byText<VendorPriceRow>((r) => r.date),
      vendor: byText<VendorPriceRow>((r) => r.vendorName),
      item: byText<VendorPriceRow>((r) => r.itemName),
      price: byNumber<VendorPriceRow>((r) => r.price),
    }),
    [],
  );
  const { sorted, head } = useSort<VendorPriceRow, LogSort>(sorters, { key: 'date', dir: -1 }, ['date', 'price']);
  const rows = sorted((data?.rows ?? []).filter((r) => hit(r.itemName)));
  const logCols: SheetColumn<VendorPriceRow>[] = [
    { key: 'date', header: head('date', t('dateCol')), label: t('dateCol'), width: '120px', render: (r) => formatDate(r.date, intlLocale, 'date') },
    { key: 'vendor', header: head('vendor', t('vendor')), label: t('vendor'), width: '170px', render: (r) => r.vendorName },
    { key: 'item', header: head('item', t('itemCol')), label: t('itemCol'), width: '240px', render: (r) => r.itemName },
    { key: 'price', header: head('price', t('priceCol')), label: t('priceCol'), width: '110px', align: 'right', render: (r) => money(r.price) },
    { key: 'unit', header: t('unitCol'), width: '70px', render: (r) => r.unit ?? r.packUnit },
    { key: 'source', header: t('sourceCol'), width: '130px', render: (r) => sourceLabel(r.source) },
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('vendorPricesTitle')}</h1>
          <p className="muted">{t('vendorPricesHint')}</p>
        </div>
      </header>
      <nav className="subnav" aria-label={t('vendorPricesTitle')}>
        <a href="#latest" className={tab === 'latest' ? 'active' : undefined} aria-current={tab === 'latest' ? 'page' : undefined} onClick={(e) => { e.preventDefault(); setTab('latest'); }}>
          {t('latestByItem')}
        </a>
        <a href="#log" className={tab === 'log' ? 'active' : undefined} aria-current={tab === 'log' ? 'page' : undefined} onClick={(e) => { e.preventDefault(); setTab('log'); }}>
          {t('allPrices')}
        </a>
      </nav>
      <div className="toolbar">
        {tab === 'log' ? <PeriodBar presets={RANGES} period={period} /> : null}
        <label className="tool">
          <span>{t('vendor')}</span>
          <select value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label={t('vendor')}>
            <option value="">{t('allVendors')}</option>
            {(allVendors.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <input className="tool-search" type="search" placeholder={t('findItem')} aria-label={t('findItem')} value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="muted">{tab === 'log' ? t('nPrices').replace('{n}', String(rows.length)) : t('nItems').replace('{n}', String(latest.length))}</span>
      </div>
      {data?.truncated && tab === 'log' ? <p className="warn-band">{t('pricesTruncated')}</p> : null}
      {tab === 'latest' && listed.some((v) => v.listDate) ? (
        <ul className="list plain" data-testid="latest-lists">
          {listed.filter((v) => v.listDate).map((v) => {
            const stale = latest.filter((l) => l.vendors.some((x) => x.vendorId === v.id && x.stale)).length;
            return (
              <li key={v.id} className={stale || v.unlinkedOnList ? 'note' : 'muted'}>
                {t('latestListLine').replace('{vendor}', v.name).replace('{date}', formatDate(v.listDate!, intlLocale))}
                {stale ? ` · ${t('staleCount').replace('{n}', String(stale))}` : ` · ${t('allFromLatest')}`}
                {v.unlinkedOnList ? (
                  <>
                    {' · '}
                    <Link to={`/items/codes?vendor=${v.id}`}>{t('unlinkedOnList').replace('{n}', String(v.unlinkedOnList))}</Link>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      <QueryView query={query}>
        {() =>
          tab === 'latest' ? (
            latest.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <Sheet label={t('latestByItem')} columns={latestCols} rows={latest} rowKey={(l) => l.itemId} rowLabel={(l) => l.itemName} stickyFirst minWidth={`${310 + vendors.length * 306}px`} />
            )
          ) : rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <Sheet label={t('allPrices')} columns={logCols} rows={rows} rowKey={(r, i) => `${r.vendorId}:${r.itemId}:${r.date}:${r.source}:${i}`} rowLabel={(r) => `${r.vendorName} ${r.itemName}`} minWidth="840px" />
          )
        }
      </QueryView>
    </div>
  );
}
